/**
 * 中文分词工具 - 基于浏览器原生 Intl.Segmenter API
 * Intl.Segmenter 是 ES2022 标准，自 2024 年 4 月起所有主流浏览器均支持
 * 基于 ICU 库实现，中文分词质量高，零依赖
 */

// 检查浏览器是否支持 Intl.Segmenter
const hasSegmenter = typeof Intl !== 'undefined' && typeof Intl.Segmenter === 'function'

// 创建中文分词器（word 粒度）
let wordSegmenter = null
if (hasSegmenter) {
  wordSegmenter = new Intl.Segmenter('zh-CN', { granularity: 'word' })
}

/**
 * 对文本进行分词
 * 优先使用 Intl.Segmenter，不支持时回退到简单的正则分词
 * @param {string} text - 待分词的文本
 * @returns {string[]} 分词结果数组（只返回有意义的词，不含空格/标点）
 */
export function segmentChinese(text) {
  if (!text || !text.trim()) return []

  if (wordSegmenter) {
    return segmentWithIntl(text)
  }
  // 降级方案：简单正则分词
  return segmentFallback(text)
}

/**
 * 使用 Intl.Segmenter 进行分词
 */
function segmentWithIntl(text) {
  const segments = wordSegmenter.segment(text)
  const results = []

  for (const { segment, isWordLike } of segments) {
    // isWordLike 为 true 表示是词语（非空格、非标点）
    if (isWordLike) {
      const trimmed = segment.trim()
      if (trimmed) {
        results.push(trimmed)
      }
    }
  }

  return results
}

/**
 * 降级方案：简单正则分词
 * 将中文按字分割，英文按空格/单词分割，数字作为整体
 */
function segmentFallback(text) {
  const results = []
  // 匹配：中文字符 | 英文单词 | 数字（含小数）
  const regex = /[\u4e00-\u9fff\u3400-\u4dbf]|[a-zA-Z]+|\d+\.?\d*/g
  let match
  while ((match = regex.exec(text)) !== null) {
    results.push(match[0])
  }
  return results
}

// 英文常见介词/虚词列表（用于过滤）
export const ENGLISH_PREPOSITIONS = new Set([
  'a', 'an', 'the',
  'in', 'on', 'at', 'to', 'for', 'of', 'with', 'by', 'from', 'up', 'about',
  'into', 'over', 'after', 'beneath', 'under', 'above', 'below', 'between',
  'through', 'during', 'before', 'behind', 'beyond', 'near', 'among', 'along',
  'across', 'around', 'against', 'within', 'without', 'upon', 'toward', 'towards',
  'onto', 'off', 'out', 'since', 'until', 'till', 'past', 'beside', 'besides',
  // 常见连词/代词/助词
  'and', 'or', 'but', 'nor', 'so', 'yet', 'not', 'no', 'as', 'if', 'than',
  'is', 'am', 'are', 'was', 'were', 'be', 'been', 'being',
  'has', 'have', 'had', 'do', 'does', 'did',
  'will', 'would', 'shall', 'should', 'may', 'might', 'can', 'could', 'must',
  'i', 'me', 'my', 'we', 'us', 'our', 'you', 'your', 'he', 'him', 'his',
  'she', 'her', 'it', 'its', 'they', 'them', 'their',
  'this', 'that', 'these', 'those', 'what', 'which', 'who', 'whom', 'whose',
  'all', 'each', 'every', 'both', 'few', 'more', 'most', 'other', 'some', 'any',
  'such', 'too', 'very', 'just', 'then', 'also', 'here', 'there', 'when', 'where',
  'how', 'why'
])
